import { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config';
import { buildTourSteps } from '../../tutorialContent';

// Global tour state — mounted once at the top of App.js, OUTSIDE <Routes>,
// so it survives navigation between steps (the whole point of a tour that
// actually walks across pages instead of staying on one).
const TutorialCtx = createContext(null);

export function useTutorial() {
    const ctx = useContext(TutorialCtx);
    if (!ctx) throw new Error('useTutorial must be used within TutorialProvider');
    return ctx;
}

function logEvent(username, event, domainKey) {
    if (!username) return;
    fetch(`${API_BASE}/auth?action=log_tutorial_event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, event, domain_key: domainKey || null }),
    }).catch(() => { /* best-effort logging, never blocks the tour */ });
}

const EMPTY_STATE = { active: false, mode: null, steps: [], currentIndex: 0 };

export function TutorialProvider({ user, children }) {
    const navigate = useNavigate();
    const [state, setState] = useState(EMPTY_STATE);

    // mode: 'grand' (first-login tour, walks every accessible workspace) or
    // a domainKey (one workspace's own tour, from its sidebar button).
    const startTour = useCallback((mode) => {
        const steps = buildTourSteps(user, mode);
        if (steps.length === 0) return;
        if (mode === 'grand') logEvent(user?.username, 'accepted', null);
        const first = steps[0];
        if (first.path && first.path !== window.location.pathname) navigate(first.path);
        setState({ active: true, mode, steps, currentIndex: 0 });
    }, [user, navigate]);

    // Shared by Next and Skip — per the design, "skip" means "skip this
    // explanation, move straight to the next step/page," not "abandon the
    // whole tour" (that's endTour, a separate button).
    const next = useCallback(() => {
        setState((s) => {
            if (!s.active) return s;
            const nextIndex = s.currentIndex + 1;
            if (nextIndex >= s.steps.length) {
                logEvent(user?.username, 'completed', s.mode === 'grand' ? null : s.mode);
                return EMPTY_STATE;
            }
            const step = s.steps[nextIndex];
            if (step.path && step.path !== window.location.pathname) navigate(step.path);
            return { ...s, currentIndex: nextIndex };
        });
    }, [navigate, user]);

    const back = useCallback(() => {
        setState((s) => {
            if (!s.active || s.currentIndex === 0) return s;
            const prevIndex = s.currentIndex - 1;
            const step = s.steps[prevIndex];
            if (step.path && step.path !== window.location.pathname) navigate(step.path);
            return { ...s, currentIndex: prevIndex };
        });
    }, [navigate]);

    const endTour = useCallback(() => {
        setState((s) => {
            if (!s.active) return s;
            const completed = s.currentIndex >= s.steps.length - 1;
            logEvent(user?.username, completed ? 'completed' : 'exited_early', s.mode === 'grand' ? null : s.mode);
            return EMPTY_STATE;
        });
    }, [user]);

    const value = {
        active: state.active,
        mode: state.mode,
        steps: state.steps,
        currentIndex: state.currentIndex,
        currentStep: state.steps[state.currentIndex] || null,
        isFirst: state.currentIndex === 0,
        isLast: state.currentIndex >= state.steps.length - 1,
        startTour,
        next,
        back,
        endTour,
        logPromptShown: () => logEvent(user?.username, 'prompt_shown', null),
        logDeclined: () => logEvent(user?.username, 'declined', null),
    };

    return <TutorialCtx.Provider value={value}>{children}</TutorialCtx.Provider>;
}
